import { useEffect, useState, CSSProperties } from "react";
import constants from "../helpers/constants";
import { isMobileDevice } from "../utils/isMobileDevice";

interface ComboLevel {
    comboLevelId: number;
    accumulatedPrice: number;
    size?: string;
    price?: number;
}

interface SelectedComboSize {
    comboLevelId: number;
    comboSize: string;
    price: number;
}

interface comboLevelDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: () => void;
    mainText: string;
    secondaryText: string;
    title: string;
    comboLevels: ComboLevel[];
    isLoading?: boolean;
    onComboLevelDetails: (details: SelectedComboSize) => void;
    comboLevelId?: number;
    comboSize?: string;
}

export default function ComboLevelDialog({
    mainText,
    secondaryText,
    comboLevels,
    title,
    onClose,
    onApply,
    isOpen,
    isLoading = false,
    onComboLevelDetails,
}: comboLevelDialogProps) {
    const [selectedComboSize, setSelectedComboSize] = useState<SelectedComboSize>({
        comboLevelId: comboLevels[0].comboLevelId,
        comboSize: constants.refComboSizeCodes[comboLevels[0].comboLevelId as keyof typeof constants.refComboSizeCodes],
        price: 0,
    });
    const [isMobile, setIsMobile] = useState<boolean>();

    useEffect(() => {
        onComboLevelDetails({
            comboLevelId: comboLevels[0].comboLevelId,
            comboSize: constants.refComboSizeCodes[comboLevels[0].comboLevelId as keyof typeof constants.refComboSizeCodes],
            price: comboLevels[0].accumulatedPrice,
        });
        const handleResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const onChangeRadioComboType = (level: number) => {
        const levelData = comboLevels.find((c) => c.comboLevelId === level);
        const price = levelData?.accumulatedPrice ?? 0;

        const updated: SelectedComboSize = {
            comboLevelId: level,
            comboSize: constants.refComboSizeCodes[level as keyof typeof constants.refComboSizeCodes],
            price,
        };

        setSelectedComboSize(updated);
        onComboLevelDetails(updated);
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay}>
            <div style={{
                width: isMobile ? "300px" : "400px",
                background: "#fff",
                borderRadius: "12px",
                padding: "15px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}>
                <h3 className="t18" style={styles.title}>{title}</h3>
                <div style={styles.options}>
                    {comboLevels.map((level) => {
                        const isSelected = selectedComboSize.comboLevelId === level.comboLevelId;
                        return (
                            <label
                                className="t18"
                                key={level.comboLevelId}
                                style={{
                                    ...styles.option,
                                }}
                            >
                                <input
                                    type="radio"
                                    name="comboLevel"
                                    value={level.comboLevelId}
                                    checked={isSelected}
                                    onChange={() => onChangeRadioComboType(level.comboLevelId)}
                                    style={{ marginRight: 5 }}
                                />
                                <span className="t18">
                                    {level.size || constants.refComboSizes[level.comboLevelId as keyof typeof constants.refComboSizeCodes]}
                                </span >
                                <small className="t16" style={{ display: "block", marginLeft: 15 }}>
                                    {level.price}
                                </small>
                            </label>
                        );
                    })}
                </div>

                <div style={styles.buttonRow}>
                    <button
                        className="t18"
                        style={{ ...styles.btn, ...styles.secondary }}
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {secondaryText}
                    </button>
                    <button
                        className="t18"
                        style={{ ...styles.btn, ...styles.primary }}
                        onClick={onApply}
                        disabled={isLoading}
                    >
                        {mainText}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles: { [key: string]: CSSProperties } = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999,
    },
    title: {
        fontSize: "18px",
        fontWeight: "bold",
        marginBottom: "16px",
        textAlign: "center",
    },
    options: {
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        marginBottom: "20px",
    },
    option: {
        flex: "1 1",
        borderRadius: "8px",
        padding: "10px",
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
    },
    optionSelected: {
        background: "#e6f0ff",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: "10px",
        marginTop: "10px",
    },
    btn: {
        flex: 1,
        padding: "10px",
        borderRadius: "6px",
        border: "none",
        cursor: "pointer",
        justifyContent: 'center',
        fontSize: "14px",
    },
    primary: {
        background: 'var(--main-turquoise)',
        color: "#fff",
    },
    secondary: {
        background: "#fff",
        border: "1px solid #ccc",
    },
};